import { useState } from "react";
import { IconPlus } from "@tabler/icons-react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import CheckboxStandalone from "../UI/CheckboxStandalone";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import DocumentCard from "../File/DocumentCard";
// @ts-expect-error pure js
import DocumentsFormUpload from "../File/DocumentsFormUpload";
// @ts-expect-error pure js
import useFiles from "../../hooks/use-files";
import {
  type DiagnosticSource,
  fileCoversDiagnostic,
} from "../../utils/documentStatus";
import type { DiagnosticRequirement } from "../../utils/diagnosticRules";
// @ts-expect-error pure js
import { FileTag } from "../../utils/constants/documents";
import type { DocumentFile } from "../../models/document";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import styles from "./DiagnosticFilesDialog.module.css";

interface UseFilesReturn {
  deleteFile: (fileId: number) => Promise<unknown>;
  updateFile: (
    fileId: number,
    values: Record<string, unknown>,
  ) => Promise<unknown>;
}

interface DiagnosticFilesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Le bien ou le dossier (immeuble) qui porte les documents. */
  property?: Property;
  propertyFolder?: PropertyFolder;
  tag: string;
  label: string;
  files: DocumentFile[];
  /** Dossier(s) dont le bien hérite les diagnostics (parent direct, documents partagés). */
  ancestors?: DiagnosticSource[];
  /** Exigence déduite des caractéristiques du bien ; absente pour un immeuble. */
  requirement?: DiagnosticRequirement;
  notApplicable?: boolean;
  onToggleNotApplicable?: (notApplicable: boolean) => Promise<void>;
  /** Appelé après tout dépôt, modification ou suppression, pour recharger la synthèse. */
  onChanged: () => void;
}

/**
 * Documents couvrant un diagnostic pour un bien ou un immeuble : PDF existants,
 * documents hérités de l'immeuble, dispense « non concerné » et dépôt d'un
 * nouveau diagnostic.
 */
export default function DiagnosticFilesDialog({
  open,
  onOpenChange,
  property,
  propertyFolder,
  tag,
  label,
  files,
  ancestors = [],
  requirement,
  notApplicable = false,
  onToggleNotApplicable,
  onChanged,
}: DiagnosticFilesDialogProps) {
  const covering = files.filter((file) => fileCoversDiagnostic(file, tag));
  const inherited = ancestors
    .map((ancestor) => ({
      name: ancestor.name,
      files: (ancestor.files ?? []).filter((file) =>
        fileCoversDiagnostic(file, tag),
      ),
    }))
    .filter((ancestor) => ancestor.files.length > 0);
  const [adding, setAdding] = useState(
    covering.length === 0 &&
      inherited.length === 0 &&
      !notApplicable &&
      requirement?.required !== false,
  );
  const [savingNotApplicable, setSavingNotApplicable] = useState(false);
  const { deleteFile, updateFile } = useFiles({
    propertyId: property?.id,
    propertyFolderId: propertyFolder?.id,
  }) as UseFilesReturn;
  const toast = useToast();

  async function handleDelete(fileId: number) {
    try {
      await deleteFile(fileId);
      onChanged();
    } catch (error) {
      toast.error({
        title: "Impossible de supprimer le document",
        description: handleAPIError(error),
      });
    }
  }

  async function handleUpdate(fileId: number, values: Record<string, unknown>) {
    const result = await updateFile(fileId, values);
    onChanged();
    return result;
  }

  async function handleToggleNotApplicable(checked: boolean) {
    if (!onToggleNotApplicable) return;
    setSavingNotApplicable(true);
    try {
      await onToggleNotApplicable(checked);
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer la dispense de diagnostic",
        description: handleAPIError(error),
      });
    } finally {
      setSavingNotApplicable(false);
    }
  }

  const requirementCallout = requirement && (
    <Callout variant={requirement.required ? "info" : "neutral"}>
      {requirement.required
        ? requirement.reason && `Diagnostic attendu : ${requirement.reason}.`
        : `Non requis pour ce bien : ${requirement.reason}. Vous pouvez tout de même conserver un document.`}
    </Callout>
  );

  const freeDocumentCallout = tag === FileTag.RISQUE_NATUREL && (
    <Callout variant="info">
      L'état des risques et pollutions se génère gratuitement, en quelques
      minutes, sur{" "}
      <a
        href="https://errial.georisques.gouv.fr/"
        target="_blank"
        rel="noreferrer"
      >
        errial.georisques.gouv.fr
      </a>{" "}
      à partir de l'adresse du bien.
    </Callout>
  );

  const notApplicableToggle = property && onToggleNotApplicable && (
    <CheckboxStandalone
      checked={notApplicable}
      disabled={savingNotApplicable}
      onChange={(e) => handleToggleNotApplicable(e.target.checked)}
    >
      Ce bien n'est pas concerné par ce diagnostic
    </CheckboxStandalone>
  );

  return (
    <ModalDialog
      open={open}
      onOpenChange={onOpenChange}
      header={label}
      subtitle={property?.nom ?? propertyFolder?.nom}
      size="lg"
    >
      {adding ? (
        <>
          <div className={styles.hints}>
            {requirementCallout}
            {freeDocumentCallout}
            {notApplicableToggle && (
              <div className={styles.notApplicable}>{notApplicableToggle}</div>
            )}
          </div>
          <DocumentsFormUpload
            property={property}
            propertyFolder={propertyFolder}
            isBailleur
            typeDocument={tag}
            onSubmitted={() => {
              onChanged();
              onOpenChange(false);
            }}
          />
        </>
      ) : (
        <>
          <ModalDialogBody className={styles.body}>
            {requirementCallout}
            {freeDocumentCallout}
            {covering.length > 0 && (
              <div className={styles.grid}>
                {covering.map((file) => (
                  <DocumentCard
                    key={file.id}
                    files={[file]}
                    titre={file.filename ?? ""}
                    tag={file.tag}
                    property={property}
                    propertyFolder={propertyFolder}
                    isBailleur
                    allowEdit
                    updateFile={handleUpdate}
                    deleteDocumentHandler={handleDelete}
                  />
                ))}
              </div>
            )}
            {inherited.map((ancestor) => (
              <div key={ancestor.name} className={styles.inherited}>
                <Text weight="medium">Hérité de {ancestor.name}</Text>
                <div className={styles.grid}>
                  {ancestor.files.map((file) => (
                    <DocumentCard
                      key={file.id}
                      files={[file]}
                      titre={file.filename ?? ""}
                      tag={file.tag}
                      isBailleur
                    />
                  ))}
                </div>
              </div>
            ))}
            {covering.length === 0 && inherited.length === 0 && (
              <Text color="muted">Aucun document pour ce diagnostic.</Text>
            )}
          </ModalDialogBody>
          <ModalDialogFooter start={notApplicableToggle}>
            <Button
              variant="secondary"
              icon={IconPlus}
              onClick={() => setAdding(true)}
            >
              Ajouter un diagnostic
            </Button>
          </ModalDialogFooter>
        </>
      )}
    </ModalDialog>
  );
}
